import React, { useState } from "react";
import styles from "../../../assets/styles/AuthComponents.module.scss";
import { setCookie } from "../../../utils/cookies";
import { Link, useNavigate } from "react-router-dom";
import Input from "../../../components/common/input/Input";
import { useAuth } from "../../../context/AuthContext";
import AuthHeader from "../../../components/common/header/auth-header/AuthHeader";
import AuthService from "../../../api/services/auth.service";
import TitleUpdater from "../../../components/common/title/TitleUpdater";
import { useTranslation } from "react-i18next";

const Login = () => {
  const { t } = useTranslation("translation");
  const { setEmail } = useAuth();
  const [emailValue, setEmailValue] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [errorMessages, setErrorMessages] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: typeof errorMessages = {};
    if (!emailValue) errors.email = t("required.email") || "";
    if (!password) errors.password = t("required.password") || "";
    if (Object.keys(errors).length > 0) {
      setErrorMessages(errors);
      return;
    }
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("email", emailValue);
      formData.append("password", password);
      setEmail(emailValue);
      const res = await AuthService.login(formData);
      setCookie("StoreDashboardAccessToken", res.data.access_token);
      navigate("/dashboard");
    } catch (error: any) {
      setLoading(false);
      const errorMsg =
        error?.response?.data?.message || error?.response?.data?.error || "";

      if (error?.response?.status === 401) {
        setErrorMessage(t("invalid_credentials") || "");
      } else if (error?.response?.status === 429) {
        setErrorMessage(
          t("error.too_many_requests") || "Please wait a while and try again.",
        );
      } else if (error?.response?.status === 403) {
        setEmail(emailValue);
        setErrorMessage(t("otp.sent_check_email") || "");
        navigate("/auth/otp");
      } else if (
        errorMsg.toLowerCase().includes("email format") ||
        errorMsg.toLowerCase().includes("correct email")
      ) {
        setErrorMessage(t("invalid_email_format") || "");
      } else {
        setErrorMessage(errorMsg || t("error_message") || "");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <TitleUpdater title={t("login.title")} />
      <form className={styles.form} onSubmit={handleSubmit}>
        <AuthHeader title={t("login.title")} />
        {errorMessage && <p className={styles.errorMessage}>{errorMessage}</p>}
        <Input
          label={t("labels.email") || ""}
          name="email"
          placeholder={t("placeholders.email") || ""}
          type="email"
          id="email"
          value={emailValue}
          onChange={(e) => {
            setEmailValue(e.target.value);
            setErrorMessage("");
            setErrorMessages((prev) => ({ ...prev, email: "" }));
          }}
          error={errorMessages.email}
        />
        <Input
          label={t("labels.password") || ""}
          name="password"
          placeholder="••••••••••"
          type="password"
          id="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setErrorMessage("");
            setErrorMessages((prev) => ({ ...prev, password: "" }));
          }}
          error={errorMessages.password}
        />
        <Link className={styles.forget} to={"/auth/forget-password"}>
          {t("login.forget_password")}
        </Link>
        <button type="submit" className={styles.button} disabled={loading}>
          {loading ? t("buttons.loading") : t("buttons.login")}
        </button>
        <div className={styles.account}>
          {t("login.no_account")}
          <Link to={"/auth/register"} className={styles.link}>
            {t("login.create_account")}
          </Link>
        </div>
      </form>
    </div>
  );
};

export default Login;
